
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'
import HotelsListItem from '@/components/HotelsListItem.vue'
import HotelsMapCard from '@/components/HotelsMapCard.vue'
import HotelsMap from '@/components/HotelsMap.vue'
import HotelsFilters from '@/components/HotelsFilters.vue'
import KayakAdInline from '@/components/KayakAdInline.vue'
import analytics from '@/helpers/analytics'

export default {
  components: {
    HotelsListItem,
    HotelsMapCard,
    HotelsMap,
    HotelsFilters,
    KayakAdInline
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      topInlineAds: 3,
      altarnateEach: 4,
      viewMode: 'list',
      listRenderLimit: 10,
      mapCardRenderLimit: 8,
      mapEnabledTracked: false,
      mapDisabledTracked: false,
      selectedHotelId: null,
      mapFiltersOpen: false,
      mobileFiltersOpen: false,
      mapBounds: null,
      viewSwitching: false
    }
  },
  created () {
    this._pendingMapSwitch = !this.$store.state.device.isMobile && !this.$store.state.device.isTablet
  },
  mounted () {
    this.setupListObserver()
  },
  beforeDestroy () {
    if (this._listObserver) {
      this._listObserver.disconnect()
    }
  },
  computed: {
    isMobile () {
      return this.$store.state.device.isMobile || this.$store.state.device.isTablet
    },
    selectedMobileHotel () {
      if (!this.selectedHotelId) return null
      return this.sortedHotelResults.find(
        h => String(h.id) === String(this.selectedHotelId)
      ) || null
    },
    activeFilterCount () {
      const checkboxes = this.$store.state.searchHotelsCheckboxes
      if (!checkboxes || typeof checkboxes !== 'object') return 0
      let unchecked = 0
      Object.values(checkboxes).forEach(group => {
        if (group && group.options) {
          Object.values(group.options).forEach(opt => {
            if (opt && !opt.checked) unchecked++
          })
        }
      })
      return unchecked
    },
    mapVisibleResults () {
      if (!this.mapBounds) return this.sortedHotelResults
      const { north, south, east, west } = this.mapBounds
      return this.sortedHotelResults.filter(h => {
        // Always keep the selected hotel visible in the list
        if (this.selectedHotelId && String(h.id) === String(this.selectedHotelId)) return true
        if (!h.location || !h.location.lat || !h.location.lon) return true
        return h.location.lat >= south && h.location.lat <= north &&
               h.location.lon >= west && h.location.lon <= east
      })
    },
    firstDeepLink () {
      if (!this.$store.state.deeplinks.length) {
        return ''
      }
      const firstLink = this.$store.state.deeplinks.filter(item => item.provider.toLowerCase() !== 'travelmedia')

      return firstLink[0] ? firstLink[0].redirect : ''
    },
    inlineAds () {
      const ads = this.$store.state.inlineAds
      if (!ads || !ads.length) return [null, null, null]
      return this.parseInlineAds(ads)
    },
    shouldShowHeading () {
      return this.hasItems || this.hasResultItems
    },
    parsedFilteredHotelsResults () {
      return this.$store.getters.parsedFilteredHotelsResults
    },
    sortedHotelResults () {
      const list = this.parsedFilteredHotelsResults.slice()
      const sort = this.$store.state.hotelSort
      const nights = Math.max(1, this.$store.state.hotelStayNights || 1)
      const local = this.$store.state.localCurrency
      const priceMode = this.$store.state.hotelPriceDisplayMode
      const priceOf = (r) => {
        const raw = local ? r.minPriceLocal : r.minPrice
        if (priceMode === 'nightly') {
          return raw / nights
        }
        return raw
      }
      const guestScore = (r) => {
        const g = parseFloat(r.guestRating)
        if (!Number.isNaN(g) && g > 0) return g
        return parseInt(r.starRating, 10) || 0
      }
      if (sort === 'price_asc') {
        list.sort((a, b) => priceOf(a) - priceOf(b))
      } else if (sort === 'price_desc') {
        list.sort((a, b) => priceOf(b) - priceOf(a))
      } else if (sort === 'rating') {
        list.sort((a, b) => guestScore(b) - guestScore(a))
      }
      return list
    },
    resultsHeading () {
      const count = this.parsedFilteredHotelsResults.length
      const q = this.$route.query || {}
      const raw = (q.destCity || q.destinationText || q.cityName || this.$t('hotels.site.object')).toString()
      let city = raw
      try {
        city = decodeURIComponent(raw.replace(/\+/g, ' '))
      } catch (e) {
        city = raw.replace(/\+/g, ' ')
      }
      return this.$t('hotels.results.heading', { city, count })
    },
    items () {
      const { sortedHotelResults, inlineAds, topInlineAds, altarnateEach } = this
      if (!sortedHotelResults.length) return []

      const results = sortedHotelResults
      const items = []
      const adCount = inlineAds.length
      const topAds = Math.min(topInlineAds, adCount)

      // Interleave top ads with first results
      const headLen = Math.min(results.length, adCount)
      for (let i = 0; i < headLen; i++) {
        items.push(inlineAds[i], results[i])
      }
      if (adCount > results.length) {
        for (let i = results.length; i < adCount; i++) {
          items.push(inlineAds[i])
        }
      }

      // Remaining results with periodic ads (no splice)
      if (results.length > adCount) {
        let adsIndex = 0
        let sinceLastAd = 0
        for (let i = adCount; i < results.length; i++) {
          sinceLastAd++
          if (topAds > 0 && sinceLastAd >= altarnateEach) {
            items.push(inlineAds[adsIndex])
            adsIndex = (adsIndex + 1) % topAds
            sinceLastAd = 0
          }
          items.push(results[i])
        }
      }

      return items.filter(Boolean)
    },
    visibleItems () {
      return this.items.slice(0, this.listRenderLimit)
    },
    visibleMapCards () {
      return this.mapVisibleResults.slice(0, this.mapCardRenderLimit)
    },
    hasMoreItems () {
      return this.listRenderLimit < this.items.length
    },
    hasMoreMapCards () {
      return this.mapCardRenderLimit < this.mapVisibleResults.length
    },
    hasResultItems () {
      return this.$store.state.searchResults.length > 0
    },
    hasItems () {
      return this.items && Object.values(this.items).length
    }
  },
  watch: {
    hasResultItems (newVal) {
      if (newVal) {
        const firstResult = this.$store.state.searchResults[0]
        this.fireAdaraEvent(firstResult)
      }
    },
    mapVisibleResults () {
      this.mapCardRenderLimit = 8
    },
    visibleItems () {
      this.$nextTick(() => this.setupListObserver())
    },
    hasItems (newVal) {
      if (newVal && this._pendingMapSwitch) {
        this._pendingMapSwitch = false
        this.setViewMode('map')
      }
    }
  },
  methods: {
    setupListObserver () {
      if (this._listObserver) {
        this._listObserver.disconnect()
      }
      const sentinel = this.$refs.listSentinel
      if (!sentinel || !window.IntersectionObserver) return
      this._listObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && this.hasMoreItems) {
          this.listRenderLimit = Math.min(this.listRenderLimit + 10, this.items.length)
        }
      }, { rootMargin: '400px' })
      this._listObserver.observe(sentinel)
    },
    onMapCardScroll (e) {
      const el = e.target
      if (this.hasMoreMapCards && el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.mapCardRenderLimit = Math.min(this.mapCardRenderLimit + 8, this.mapVisibleResults.length)
      }
    },
    setViewMode (mode) {
      if (mode === this.viewMode) return
      this.viewSwitching = true
      this.$emit('viewChange', mode)
      // Force the overlay to paint, then do the heavy switch
      this.$nextTick(() => {
        setTimeout(() => {
          this.viewMode = mode
          if (mode === 'map') {
            if (!this.mapEnabledTracked) {
              this.mapEnabledTracked = true
              analytics.track('hotels-map-enabled', this)
            }
            const resizeMap = () => {
              const mapRef = this.isMobile ? this.$refs.hotelsMapMobile : this.$refs.hotelsMap
              if (mapRef && mapRef.map) {
                mapRef.map.resize()
                if (mapRef.hotels && mapRef.hotels.length) {
                  mapRef.fitAll()
                }
              }
            }
            this.$nextTick(() => {
              setTimeout(resizeMap, 150)
              setTimeout(resizeMap, 500)
            })
          } else if (mode === 'list' && !this.mapDisabledTracked) {
            this.mapDisabledTracked = true
            analytics.track('hotels-map-disabled', this)
          }
          this.$nextTick(() => {
            setTimeout(() => { this.viewSwitching = false }, 150)
          })
        }, 30)
      })
    },
    selectHotelFromMapMobile (hotelId) {
      if (hotelId === null) {
        this.selectedHotelId = null
        return
      }
      if (String(this.selectedHotelId) === String(hotelId)) {
        this.selectedHotelId = null
      } else {
        this.selectedHotelId = hotelId
      }
    },
    openMobileHotelDeal (hotelId) {
      const hotel = this.sortedHotelResults.find(h => String(h.id) === String(hotelId))
      if (hotel && hotel.rooms && hotel.rooms[0] && hotel.rooms[0].partnerDeeplinkUrl) {
        window.open(hotel.rooms[0].partnerDeeplinkUrl, '_blank')
      }
    },
    selectHotel (hotelId) {
      this.selectedHotelId = hotelId
    },
    selectHotelFromMap (hotelId) {
      this.selectedHotelId = hotelId
      this.scrollToCard(hotelId)
    },
    showOnMap (hotelId) {
      this.selectedHotelId = hotelId
      this.$nextTick(() => {
        const map = this.$refs.hotelsMap
        if (map && map.panTo) {
          map.panTo(hotelId)
        }
      })
      this.scrollToCard(hotelId)
    },
    scrollToCard (hotelId) {
      const tryScroll = () => {
        const container = this.$refs.mapCardList
        if (!container) return false
        const el = container.querySelector('[data-hotel-id="' + hotelId + '"]')
        if (el) {
          const offset = el.offsetTop - container.offsetTop
          container.scrollTo({ top: Math.max(0, offset - 10), behavior: 'smooth' })
          return true
        }
        container.scrollTop = 0
        return false
      }
      this.$nextTick(() => {
        tryScroll()
        setTimeout(tryScroll, 500)
        setTimeout(tryScroll, 1000)
      })
    },
    onCardHover (hotelId) {
      const map = this.$refs.hotelsMap
      if (map && map.highlightPin) map.highlightPin(hotelId)
    },
    onCardUnhover (hotelId) {
      const map = this.$refs.hotelsMap
      if (map && map.unhighlightPin) map.unhighlightPin(hotelId)
    },
    fireAdaraEvent (item) {
      if (Object.keys(item).length === 0) return

      const adaraParams = {
      }
      try {
        window.adara('send', {
          pageType: 'Results Item',
          siteLanguage: this.$store.state.language,
          siteCountry: this.$store.state.country,
          ...adaraParams
        })
      } catch (e) {
        console.info('Error firing Adara event')
      }
    },
    parseInlineAds (ads) {
      return ads.map((ad, index) => ({ ...ad, id: `kayak-${index}`, component: 'KayakAdInline' }))
    },
    handleNoResultsClick () {
      analytics.track('meta-hotel-no-results-click', this)
    },
    openCompareModal (item) {
      this.$emit('openCompareModal', { hotel: item })
    },
    compareAll (data) {
      this.$emit('compareAll', data)
    }
  }
}
